/**
 * /ja/voice-recorder-transcription — ボイスレコーダー＋文字起こし bridge (JA).
 *
 * Targets ボイスレコーダー 文字起こし (7k) / ai ボイスレコーダー (2.8k) /
 * 録音 文字起こし アプリ (1.8k). No EN sibling.
 */
import { MarketingShell } from './MarketingShell';

export function JaVoiceRecorderTranscription() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/voice-recorder-transcription"
      hreflangLocales={['ja']}
      documentTitle="文字起こしできるボイスレコーダー｜AIレコーダー活用術 · Draftlize"
      documentDescription="録音と同時に文字起こしできるAIボイスレコーダーの選び方。録音した会議を「決定の記録」に変える使い方まで紹介します。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · AIレコーダー</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            文字起こし対応ボイスレコーダーの<br />
            <em>選び方と、活かし方。</em>
          </h1>
          <p className="lede">
            録音と同時にテキスト化してくれる AI ボイスレコーダーは、会議や打ち合わせの記録をぐっと楽にします。でも、レコーダーが出すのは録音と文字起こし——つまり<em>素材</em>です。録音した会議の価値は、全発言ではなく、その中で下した<em>決定</em>にあります。ここでは文字起こしできるレコーダーの選び方と、録音を「決定の記録」に変える使い方を紹介します。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">文字起こしできるレコーダーの選び方。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>専用機か、アプリか</strong>——手軽さならスマホアプリ、長時間・高音質なら専用の AI レコーダー。</li>
            <li><strong>文字起こしの精度と対応言語</strong>——日本語の精度と話者分離。</li>
            <li><strong>書き出し形式</strong>——テキストや要約を外に出せるか。<em>ここが後工程の鍵</em>。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            録音 → 文字起こし、まではどのツールでもできます。問題はその先。<a href="/ja/meeting-minutes-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>議事録</a>にまとめても、決定は数週間で古くなります。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">録音 vs 決定</span>
            <h2 className="serif">録音は素材。<em>決定は成果。</em></h2>
          </div>
          <p>AI レコーダーと Draftlize は別の仕事をします。片方は録って文字化し、もう片方は決定を生かし続ける。両方使えばいい。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>AIボイスレコーダー</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>仕事</td><td>録って文字にする</td><td className="ours yes">決定を最新に保つ</td></tr>
            <tr><td>出力</td><td className="partial">録音＋文字起こし</td><td className="ours yes">決定カードのグラフ</td></tr>
            <tr><td>決定が変わる</td><td className="no">古い録音が残るだけ</td><td className="ours yes">依存先が自動で stale</td></tr>
            <tr><td>AI エージェント</td><td className="partial">音声・文字を渡す</td><td className="ours yes">MCP で決定を読む</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          録音は完璧に残る。ただ「その会議で決めたことが今も有効か」は、録音ファイルには書いていない。
        </blockquote>
        <cite>レコーダーはそのまま。決定を生かすのが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '文字起こしできるレコーダーは何がいい？', a: '手軽さ重視ならスマホの文字起こしアプリ、長時間・高音質なら専用 AI レコーダー。Draftlize は録音・文字起こしはせず、その後の決定をカード化する役割です。' },
            { q: 'アプリだけで十分？', a: '記録目的なら十分です。ただ、録音した会議の決定を後から使いたいなら、文字起こしの先に「決定を残す」工程が要ります。' },
            { q: '録音データはどう活用する？', a: '文字起こしや要約を Draftlize に渡し、決定をカードに。全録音を抱えるのではなく、会議より長生きすべき数個の決定を最新に保ちます。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">録音を、決定の記録に変える<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            AI レコーダーはそのまま。文字起こしを Draftlize に渡せば、録音した会議の決定が、リンクされ最新で AI も読めるカードになります。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
